import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  SealCheck,
  MapPin,
  ChatCircle,
  PaperPlaneTilt,
  ShareNetwork,
  GridFour,
  SquareHalf,
  Info,
  Storefront,
  HeartStraight,
  MusicNote,
  Trophy,
  Play,
  Pause,
  Clock,
  Newspaper,
  SpeakerHigh,
  SpeakerX,
  SkipForward,
  SkipBack,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import {
  getArtist,
  getPostsByArtist,
  posts as allPosts,
  products,
  getTiers,
  getTracks,
  getApplications,
  getCredentials,
  getCall,
  calls as allCalls,
} from "@/data/mockData";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EditProfileDrawer from "@/components/EditProfileDrawer";
import MusicManagerDrawer from "@/components/MusicManagerDrawer";
import TipDrawer from "@/components/TipDrawer";
import SubscribeDrawer from "@/components/SubscribeDrawer";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";

const baseTabs = [
  { key: "posts", label: "Posts", icon: GridFour },
  { key: "portfolio", label: "Portafolio", icon: SquareHalf },
  { key: "shop", label: "Tienda", icon: Storefront },
  { key: "subscribe", label: "Suscribirse", icon: HeartStraight },
  { key: "about", label: "Info", icon: Info },
];

export default function ArtistProfile() {
  const { slug } = useParams();
  const { user: me } = useAuth();

  // Fetch real user + posts from the API, fall back to mock if not found yet.
  const [remoteUser, setRemoteUser] = useState(null);
  const [remotePosts, setRemotePosts] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingMusic, setEditingMusic] = useState(false);
  const [tipping, setTipping] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const payment = usePaymentStatus();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/users/${slug}`).catch(() => null),
      api.get(`/posts`, { params: { handle: slug } }).catch(() => null),
    ]).then(([u, p]) => {
      if (cancelled) return;
      if (u?.data?.user) {
        setRemoteUser(u.data.user);
        setIsFollowing(Boolean(u.data.is_following));
      } else {
        setRemoteUser(false);
      }
      setRemotePosts(p?.data?.posts || []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const mockArtist = getArtist(slug);
  const isOwner = !!(me && remoteUser && me.id === remoteUser.id);
  // Build a compat artist object that matches what the rest of the UI expects.
  const artist = remoteUser
    ? {
        slug: remoteUser.handle,
        handle: remoteUser.handle,
        name: remoteUser.name,
        role:
          (remoteUser.type === "musician" && "Músicx · compositorx") ||
          (remoteUser.primary_discipline || "")
            .replace(/-/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase()),
        city: remoteUser.city
          ? `${remoteUser.city} · ${remoteUser.country || ""}`.trim()
          : "",
        discipline: (remoteUser.primary_discipline || "").replace(/-/g, " ").toUpperCase(),
        avatar: remoteUser.photo || mockArtist?.avatar,
        cover: remoteUser.cover || mockArtist?.cover,
        bio: remoteUser.short_bio || remoteUser.long_bio || "",
        followers: remoteUser.followers || 0,
        following: remoteUser.following || 0,
        posts: remoteUser.posts || 0,
        verified: remoteUser.verified,
      }
    : mockArtist;

  // Music data: prefer backend tracks; fall back to mock fixtures.
  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const mediaUrl = (u) => (u && u.startsWith("/api/") ? `${backendBase}${u}` : u);
  const musicData = (() => {
    const t = remoteUser?.tracks || [];
    if (t.length > 0) {
      const rel = remoteUser?.music_release || {};
      return {
        title: rel.title || `${artist?.name || "Untitled"}`,
        type: rel.type || "EP",
        year: rel.year || new Date().getFullYear(),
        cover: mediaUrl(rel.cover) || artist?.cover,
        tracks: t.map((x, i) => ({
          id: x.id || `track-${i}`,
          title: x.title,
          duration: x.duration || "—",
          plays: Number(x.plays) || 0,
          bpm: x.bpm ?? "—",
          key: x.key || "—",
          explicit: !!x.explicit,
          featured: !!x.featured,
          audio_url: mediaUrl(x.audio_url),
        })),
      };
    }
    return getTracks(slug);
  })();
  const applications = getApplications(slug);
  const tabs = useMemo(() => {
    const list = [...baseTabs];
    if (musicData) {
      list.splice(1, 0, { key: "music", label: "Música", icon: MusicNote });
    }
    if (applications.length > 0) {
      list.splice(musicData ? 4 : 3, 0, {
        key: "applications",
        label: "Postulaciones",
        icon: Trophy,
      });
    }
    return list;
  }, [musicData, applications.length]);
  const [tab, setTab] = useState(musicData ? "music" : "posts");

  const artistPosts = remotePosts && remotePosts.length > 0 ? remotePosts : getPostsByArtist(slug);
  // fallback: if artist has no posts, show some for demo
  const gridPosts =
    artistPosts.length > 0 ? artistPosts : allPosts.filter((p) => p.image).slice(0, 6);

  const onToggleFollow = async () => {
    if (!me) {
      window.location.assign("/ingresar");
      return;
    }
    if (!remoteUser || remoteUser.id === me.id) return;
    const prev = isFollowing;
    setIsFollowing(!prev);
    try {
      if (prev) await api.delete(`/users/${slug}/follow`);
      else await api.post(`/users/${slug}/follow`);
    } catch {
      setIsFollowing(prev);
    }
  };

  if (loading && !mockArtist) {
    return (
      <Layout>
        <div className="p-10 font-mono text-[11px] uppercase tracking-[0.3em] text-[#A1A1AA]">
          cargando perfil…
        </div>
      </Layout>
    );
  }

  if (!artist) {
    return (
      <Layout>
        <div data-testid="profile-not-found" className="p-10">
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em]">// 404</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-2">
            Artista no encontrade
          </h1>
          <p className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-2">
            El handle @{slug} no existe en el directorio.
          </p>
          <Link
            to="/explorar"
            className="inline-block mt-6 btn-brutal bg-[#FF3E00] text-white font-mono uppercase text-xs tracking-[0.22em] font-bold px-5 py-2"
          >
            ← Volver al directorio
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="profile-page">
        {/* Cover */}
        <div className="relative aspect-[3/1] md:aspect-[4/1] bg-[#121212] border-b border-[#27272A] overflow-hidden">
          <img
            src={artist.cover}
            alt="cover"
            className="w-full h-full object-cover grayscale opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
        </div>

        {/* Identity */}
        <section className="px-4 md:px-8 -mt-16 md:-mt-20 relative">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div className="h-32 w-32 md:h-40 md:w-40 overflow-hidden border-4 border-[#050505] bg-[#121212]">
              <img
                src={artist.avatar}
                alt={artist.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex items-center gap-2 mt-4 md:mt-0">
              {isOwner ? (
                <>
                  <button
                    data-testid="edit-profile-btn"
                    onClick={() => setEditingProfile(true)}
                    className="btn-brutal font-mono uppercase text-xs tracking-[0.22em] px-6 py-3 bg-[#FF3E00] text-white"
                  >
                    Editar perfil
                  </button>
                  {remoteUser?.type === "musician" || remoteUser?.primary_discipline === "musica" ? (
                    <button
                      data-testid="edit-music-btn"
                      onClick={() => setEditingMusic(true)}
                      className="btn-brutal font-mono uppercase text-xs tracking-[0.22em] px-4 py-3 border border-[#FF3E00] text-[#FF3E00] hover:bg-[#FF3E00] hover:text-white inline-flex items-center gap-2"
                    >
                      <MusicNote size={14} weight="bold" /> Música
                    </button>
                  ) : null}
                </>
              ) : (
                <>
                  <button
                    data-testid="follow-btn"
                    onClick={onToggleFollow}
                    className={`btn-brutal font-mono uppercase text-xs tracking-[0.22em] px-6 py-3 ${
                      isFollowing
                        ? "bg-transparent border border-[#F4F4F5] text-[#F4F4F5]"
                        : "bg-[#FF3E00] text-white"
                    }`}
                  >
                    {isFollowing ? "Siguiendo" : "+ Seguir"}
                  </button>
                  <Link
                    to={`/mensajes?with=${artist.handle || artist.slug}`}
                    data-testid="message-btn"
                    className="btn-brutal font-mono uppercase text-xs tracking-[0.22em] px-4 py-3 border border-[#27272A] text-[#F4F4F5] hover:border-[#FF3E00] hover:text-[#FF3E00] inline-flex items-center gap-2"
                  >
                    <PaperPlaneTilt size={14} weight="bold" /> Mensaje
                  </Link>
                  <button
                    data-testid="tip-btn"
                    onClick={() => (me ? setTipping(true) : null)}
                    title={me ? "Enviar propina" : "Inicia sesión para propinar"}
                    disabled={!me}
                    className="btn-brutal font-mono uppercase text-xs tracking-[0.22em] px-4 py-3 bg-[#00FF66] text-black hover:bg-white inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    <HeartStraight size={14} weight="fill" /> Tip
                  </button>
                </>
              )}
              <button
                aria-label="Compartir"
                className="h-11 w-11 border border-[#27272A] hover:border-[#FF3E00] inline-flex items-center justify-center text-[#A1A1AA] hover:text-[#FF3E00]"
              >
                <ShareNetwork size={16} />
              </button>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-3xl md:text-5xl font-black tracking-tighter uppercase leading-none">
                {artist.name}
              </h1>
              {artist.verified ? (
                <SealCheck
                  size={24}
                  weight="fill"
                  className="text-[#FF3E00]"
                />
              ) : null}
            </div>
            <div className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-3">
              @{artist.handle} · {artist.role.toUpperCase()}
            </div>
            <p className="mt-5 text-[15px] leading-[1.7] text-[#E5E5E5] max-w-2xl">
              {artist.bio}
            </p>

            <div className="flex flex-wrap items-center gap-5 mt-5 font-mono text-[11px] tracking-wider">
              <span className="inline-flex items-center gap-1.5 text-[#A1A1AA]">
                <MapPin size={13} /> {artist.city.toUpperCase()}
              </span>
              <span className="inline-flex items-center gap-1.5 text-[#A1A1AA]">
                <span className="h-1.5 w-1.5 bg-[#00FF66]" />
                ACTIVA · ACEPTA COLABS
              </span>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 mt-8 border border-[#27272A]">
            <Stat n={artist.posts} label="PUBLICACIONES" />
            <Stat n={artist.followers.toLocaleString("es")} label="SEGUIDORES" divider />
            <Stat n={artist.following.toLocaleString("es")} label="SIGUIENDO" divider />
          </div>
        </section>

        {/* Tabs */}
        <nav className="mt-8 border-b border-[#27272A] flex">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex-1 flex items-center justify-center gap-2 py-4 font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
                  active
                    ? "border-[#FF3E00] text-[#FF3E00]"
                    : "border-transparent text-[#A1A1AA] hover:text-white"
                }`}
              >
                <Icon size={14} weight={active ? "fill" : "regular"} />
                {t.label}
              </button>
            );
          })}
        </nav>

        {/* Tab content */}
        <section className="p-3 md:p-4">
          {tab === "posts" && (
            <div className="grid grid-cols-3 gap-1 md:gap-2">
              {gridPosts.map((p, i) => (
                <Link
                  to={`/post/${p.id}`}
                  key={p.id + i}
                  className="group relative aspect-square bg-[#121212] overflow-hidden"
                >
                  <img
                    src={p.image}
                    alt=""
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-4 opacity-0 group-hover:opacity-100">
                    <span className="font-mono text-xs text-white flex items-center gap-1">
                      ♥ {p.likes.toLocaleString("es")}
                    </span>
                    <span className="font-mono text-xs text-white flex items-center gap-1">
                      <ChatCircle size={14} /> {p.comments}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {tab === "portfolio" && (
            <PortfolioTab
              isOwner={me && remoteUser && me.id === remoteUser.id}
              works={remoteUser?.works || []}
              fallbackPosts={gridPosts}
              onChange={(nextWorks) => {
                setRemoteUser((u) => (u ? { ...u, works: nextWorks } : u));
              }}
            />
          )}

          {tab === "shop" && <ShopTab slug={artist.slug} />}
          {tab === "music" && musicData && <MusicTab data={musicData} />}
          {tab === "applications" && applications.length > 0 && (
            <ApplicationsTab slug={artist.slug} applications={applications} />
          )}
          {tab === "subscribe" && (
            <SubscribeTab
              slug={artist.slug}
              artist={artist}
              onSubscribe={() => setSubscribing(true)}
            />
          )}

          {tab === "about" && (
            <div className="max-w-3xl grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
              <InfoRow k="DISCIPLINA" v={artist.discipline} />
              <InfoRow k="BASE" v={artist.city} />
              <InfoRow k="MIEMBRO DESDE" v="2023" />
              <InfoRow k="IDIOMAS" v="ES · EN · PT" />
              <InfoRow k="CONTACTO" v={`${artist.handle}@elarte.net`} />
              <InfoRow k="REPRESENTACIÓN" v="Galería Helga de Alvear" />
              <div className="md:col-span-2">
                <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.25em] mb-2">
                  // MANIFIESTO
                </div>
                <p className="font-display text-xl md:text-2xl leading-[1.4]">
                  “{artist.bio}”
                </p>
              </div>
            </div>
          )}
        </section>
      </div>
      {editingProfile && remoteUser ? (
        <EditProfileDrawer
          user={remoteUser}
          onClose={() => setEditingProfile(false)}
          onSaved={(u) => setRemoteUser(u)}
        />
      ) : null}
      {editingMusic && remoteUser ? (
        <MusicManagerDrawer
          user={remoteUser}
          onClose={() => setEditingMusic(false)}
          onSaved={(u) => setRemoteUser(u)}
        />
      ) : null}
      {tipping && remoteUser ? (
        <TipDrawer artist={remoteUser} onClose={() => setTipping(false)} />
      ) : null}
      {subscribing && remoteUser ? (
        <SubscribeDrawer
          artist={remoteUser}
          onClose={() => setSubscribing(false)}
        />
      ) : null}

      {/* Payment status banner (when returning from Stripe) */}
      {payment.state === "polling" || payment.state === "paid" || payment.state === "failed" ? (
        <div
          data-testid="payment-banner"
          className={`fixed top-4 right-4 z-[90] max-w-sm border px-4 py-3 font-mono text-[12px] tracking-wider ${
            payment.state === "paid"
              ? "bg-[#00FF66]/10 border-[#00FF66] text-[#00FF66]"
              : payment.state === "failed"
              ? "bg-[#FF0033]/10 border-[#FF0033] text-[#FF8899]"
              : "bg-[#FF3E00]/10 border-[#FF3E00] text-[#FFAA88]"
          }`}
        >
          {payment.state === "polling" && "· Verificando pago…"}
          {payment.state === "paid" &&
            `✓ Pago confirmado · ${
              payment.kind === "tip"
                ? "propina enviada"
                : payment.kind === "order"
                ? "compra realizada"
                : payment.kind === "subscription"
                ? "suscripción activa"
                : "gracias"
            }`}
          {payment.state === "failed" && `Error: ${payment.error}`}
        </div>
      ) : null}
    </Layout>
  );
}

function Stat({ n, label, divider, color }) {
  return (
    <div
      className={`p-4 md:p-5 text-center ${
        divider ? "border-l border-[#27272A]" : ""
      }`}
    >
      <div
        className="font-display font-black text-2xl md:text-3xl tracking-tight"
        style={color ? { color } : undefined}
      >
        {n}
      </div>
      <div className="font-mono text-[9px] md:text-[10px] text-[#A1A1AA] tracking-[0.22em] mt-1">
        {label}
      </div>
    </div>
  );
}

function InfoRow({ k, v }) {
  return (
    <div className="border border-[#27272A] p-4">
      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-2">
        {k}
      </div>
      <div className="font-display font-semibold">{v}</div>
    </div>
  );
}

function PortfolioTab({ isOwner, works, fallbackPosts, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const mediaUrl = (u) => (u && u.startsWith("/api/") ? `${backendBase}${u}` : u);

  const addWork = async (file) => {
    if (!file) return;
    setErr("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newWork = {
        id: `w_${Date.now()}`,
        title: file.name?.split(".")[0] || `Obra ${works.length + 1}`,
        image: up.url,
      };
      const next = [...works, newWork];
      const { data: u } = await api.patch("/users/me", { works: next });
      onChange(u.user?.works || next);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "No pudimos subir la obra");
    } finally {
      setBusy(false);
    }
  };

  const removeWork = async (id) => {
    setErr("");
    const next = works.filter((w) => w.id !== id);
    try {
      const { data: u } = await api.patch("/users/me", { works: next });
      onChange(u.user?.works || next);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= works.length) return;
    const next = works.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
    try {
      await api.patch("/users/me", { works: next });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };

  if (!works || works.length === 0) {
    if (isOwner) {
      return (
        <div className="p-4 md:p-6">
          <div className="border-2 border-dashed border-[#27272A] bg-[#0a0a0a] p-10 text-center">
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-3">
              // PORTFOLIO VACÍO
            </div>
            <h3 className="font-display text-3xl font-black uppercase tracking-tight mb-2">
              Sube tus primeras obras.
            </h3>
            <p className="font-mono text-[11px] text-[#A1A1AA] tracking-wider max-w-md mx-auto mb-6">
              MÁX 20MB · JPG · PNG · WEBP. APARECERÁN EN TU PERFIL PÚBLICO Y EN TU KIT DE PRENSA.
            </p>
            <label
              data-testid="portfolio-first-upload"
              className="btn-brutal inline-flex items-center gap-2 bg-[#FF3E00] text-white font-mono uppercase text-xs tracking-[0.22em] font-bold px-6 py-3 cursor-pointer"
            >
              {busy ? "Subiendo…" : "+ Añadir obra"}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => addWork(e.target.files?.[0])}
                className="hidden"
              />
            </label>
            {err ? (
              <div className="mt-4 font-mono text-[11px] text-[#FF8899]">{err}</div>
            ) : null}
          </div>
        </div>
      );
    }
    // Guest view without works → fallback to post grid
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
        {fallbackPosts.slice(0, 4).map((p, i) => {
          const src = p.media_url || p.image;
          return (
            <div
              key={(p.id || i) + "-fp"}
              className="relative aspect-[4/5] bg-[#121212] overflow-hidden border border-[#27272A]"
            >
              {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : null}
              <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black to-transparent">
                <div className="font-display font-bold">{(p.caption || "").slice(0, 60)}…</div>
                <div className="font-mono text-[10px] text-[#FF3E00] mt-1">
                  {(p.tags || []).join(" · ")}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="p-3 md:p-5 space-y-4">
      {isOwner ? (
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-1">
              // MI PORTFOLIO · {works.length} {works.length === 1 ? "OBRA" : "OBRAS"}
            </div>
            <p className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
              Arrastra los controles ← → para reordenar.
            </p>
          </div>
          <label
            data-testid="portfolio-add-work"
            className="btn-brutal inline-flex items-center gap-2 bg-[#FF3E00] text-white font-mono uppercase text-xs tracking-[0.22em] font-bold px-4 py-2 cursor-pointer"
          >
            {busy ? "Subiendo…" : "+ Añadir"}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => addWork(e.target.files?.[0])}
              className="hidden"
            />
          </label>
        </div>
      ) : null}

      {err ? (
        <div className="font-mono text-[11px] text-[#FF8899] border border-[#FF0033] bg-[#FF0033]/10 px-3 py-2">
          {err}
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {works.map((w, i) => (
          <div
            key={w.id || i}
            className="group relative aspect-square bg-[#121212] overflow-hidden border border-[#27272A]"
          >
            {w.image ? (
              <img
                src={mediaUrl(w.image)}
                alt={w.title || "obra"}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : null}
            <div className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-[0.22em] bg-black/70 text-white px-1.5 py-0.5">
              {String(i + 1).padStart(2, "0")}
            </div>
            {w.title ? (
              <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                <div className="font-display font-bold text-sm truncate">{w.title}</div>
              </div>
            ) : null}
            {isOwner ? (
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 flex items-center justify-center gap-2">
                <button
                  title="Mover antes"
                  onClick={() => move(i, -1)}
                  className="h-8 w-8 bg-black text-white border border-[#27272A] hover:border-[#FF3E00]"
                >
                  ←
                </button>
                <button
                  title="Mover después"
                  onClick={() => move(i, 1)}
                  className="h-8 w-8 bg-black text-white border border-[#27272A] hover:border-[#FF3E00]"
                >
                  →
                </button>
                <button
                  data-testid={`portfolio-remove-${i}`}
                  title="Quitar"
                  onClick={() => removeWork(w.id)}
                  className="h-8 w-8 bg-[#FF0033] text-white inline-flex items-center justify-center"
                >
                  ×
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function ShopTab({ slug }) {
  const items = products.filter((p) => p.artist.slug === slug);
  if (items.length === 0) {
    return (
      <div className="p-10 text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">
          · sin ítems en la tienda ·
        </div>
      </div>
    );
  }
  return (
    <div className="p-3 md:p-4 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
      {items.map((p) => (
        <Link
          key={p.id}
          to={`/tienda/${p.id}`}
          data-testid={`artist-shop-${p.id}`}
          className="group block border border-[#27272A] hover:border-[#FF3E00] bg-[#0a0a0a]"
        >
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src={p.image}
              alt={p.title}
              className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
            />
            <span className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-[0.22em] bg-[#FF3E00] text-white px-1.5 py-0.5">
              {p.type.toUpperCase()}
            </span>
          </div>
          <div className="p-3 flex items-center justify-between border-t border-[#27272A] gap-2">
            <span className="font-display font-bold text-sm truncate">
              {p.title}
            </span>
            <span className="font-display font-black text-[#FF3E00] whitespace-nowrap">
              {p.currency === "ETH"
                ? `Ξ${p.price}`
                : `${p.currency === "USD" ? "$" : "€"}${p.price.toLocaleString("es")}`}
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}

function SubscribeTab({ slug, artist, onSubscribe }) {
  const tiers = getTiers(slug);
  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
          SUSCRÍBETE A {artist.name.toUpperCase()}
        </div>
        <h2 className="font-display text-2xl md:text-4xl font-black tracking-tighter uppercase">
          Apoya el trabajo, recibe algo único.
        </h2>
        <p className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-3 max-w-2xl">
          // EL 92% VA DIRECTO AL ARTISTA. CANCELA CUANDO QUIERAS.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tiers.map((t) => (
          <div
            key={t.id}
            data-testid={`tier-${t.id}`}
            className={`border p-5 flex flex-col transition-colors ${
              t.featured
                ? "border-[#FF3E00] bg-[#FF3E00]/5"
                : "border-[#27272A] bg-[#0a0a0a] hover:border-[#F4F4F5]"
            }`}
          >
            {t.featured ? (
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] bg-[#FF3E00] text-white px-2 py-1 self-start mb-4">
                MÁS POPULAR
              </span>
            ) : (
              <span className="h-[22px] mb-4" />
            )}
            <h3 className="font-display font-black text-2xl uppercase tracking-tight">
              {t.name}
            </h3>
            <div className="mt-3">
              <span className="font-display font-black text-5xl text-[#FF3E00]">
                €{t.price}
              </span>
              <span className="font-mono text-[11px] text-[#A1A1AA] tracking-wider ml-1">
                / MES
              </span>
            </div>
            <ul className="mt-5 space-y-2 text-sm text-[#E5E5E5] flex-1">
              {t.perks.map((perk) => (
                <li
                  key={perk}
                  className="flex items-start gap-2 font-display leading-snug"
                >
                  <span className="text-[#00FF66] mt-1 shrink-0">✓</span>
                  {perk}
                </li>
              ))}
            </ul>
            <button
              data-testid={`subscribe-${t.id}`}
              onClick={onSubscribe}
              className={`btn-brutal mt-5 font-mono uppercase text-[10px] tracking-[0.22em] py-3 font-bold ${
                t.featured
                  ? "bg-[#FF3E00] text-white"
                  : "border border-[#F4F4F5] text-[#F4F4F5] hover:bg-[#F4F4F5] hover:text-black"
              }`}
            >
              Suscribirme con Stripe
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


const FALLBACK_AUDIO_POOL = [
  "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
  "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3",
  "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3",
  "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3",
  "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-5.mp3",
  "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-6.mp3",
];

function fmt(sec) {
  if (!isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function parseDurStr(s) {
  if (!s) return 0;
  const parts = String(s).split(":").map((n) => Number(n) || 0);
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
}

function MusicTab({ data }) {
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const [muted, setMuted] = useState(false);

  const total = data.tracks.length;
  const totalPlays = data.tracks.reduce((a, t) => a + t.plays, 0);

  const playingIdx = data.tracks.findIndex((t) => t.id === playingId);
  const currentTrack = playingIdx >= 0 ? data.tracks[playingIdx] : null;

  const resolveAudio = (t, idx) =>
    t.audio_url && t.audio_url.length > 0
      ? t.audio_url
      : FALLBACK_AUDIO_POOL[idx % FALLBACK_AUDIO_POOL.length];

  const playTrack = (id) => {
    const el = audioRef.current;
    if (!el) return;
    if (playingId === id) {
      if (el.paused) el.play().catch(() => {});
      else el.pause();
      return;
    }
    const idx = data.tracks.findIndex((t) => t.id === id);
    const t = data.tracks[idx];
    if (!t) return;
    el.src = resolveAudio(t, idx);
    el.currentTime = 0;
    setPlayingId(id);
    setCurrentTime(0);
    setDuration(0);
    el.play().catch(() => {});
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const v = Number(e.target.value);
    el.currentTime = v;
    setCurrentTime(v);
  };

  const next = () => {
    if (playingIdx < 0) return;
    const n = (playingIdx + 1) % data.tracks.length;
    playTrack(data.tracks[n].id);
  };
  const prev = () => {
    if (playingIdx < 0) return;
    const p = (playingIdx - 1 + data.tracks.length) % data.tracks.length;
    playTrack(data.tracks[p].id);
  };

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setCurrentTime(el.currentTime);
    const onMeta = () => setDuration(el.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnd = () => {
      setIsPlaying(false);
      next();
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playingIdx]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.volume = volume;
    el.muted = muted;
  }, [volume, muted]);

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="pb-28">
      {/* Hidden audio element — shared across all tracks */}
      <audio ref={audioRef} preload="metadata" data-testid="music-audio" />

      {/* Album header */}
      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-6 p-4 md:p-6 border-b border-[#27272A]">
        <div className="relative aspect-square bg-[#121212] overflow-hidden max-w-[320px]">
          <img
            src={data.cover}
            alt={data.title}
            className="w-full h-full object-cover"
          />
          <button
            data-testid="play-album-hero"
            onClick={() => playTrack(data.tracks[0]?.id)}
            className="absolute bottom-3 right-3 h-14 w-14 bg-[#FF3E00] text-white flex items-center justify-center hover:scale-110 transition-transform"
          >
            {isPlaying && playingIdx >= 0 ? (
              <Pause size={24} weight="fill" />
            ) : (
              <Play size={24} weight="fill" />
            )}
          </button>
        </div>
        <div className="flex flex-col justify-between">
          <div>
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
              {data.type} · {data.year}
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter uppercase leading-[0.95]">
              {data.title}
            </h2>
            <div className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-3">
              {total} TRACKS · {(totalPlays / 1000).toFixed(0)}K PLAYS TOTALES
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <button
              data-testid="play-album"
              onClick={() => playTrack(data.tracks[0]?.id)}
              className="btn-brutal bg-[#FF3E00] text-white font-mono uppercase text-xs tracking-[0.22em] font-bold px-6 py-3 inline-flex items-center gap-2"
            >
              {isPlaying ? (
                <>
                  <Pause size={14} weight="fill" /> Pausar
                </>
              ) : (
                <>
                  <Play size={14} weight="fill" /> Reproducir
                </>
              )}
            </button>
            <button className="btn-brutal border border-[#F4F4F5] font-mono uppercase text-xs tracking-[0.22em] px-4 py-3 hover:bg-[#F4F4F5] hover:text-black">
              + Biblioteca
            </button>
            <button className="btn-brutal border border-[#27272A] font-mono uppercase text-xs tracking-[0.22em] px-4 py-3 hover:border-[#FF3E00] hover:text-[#FF3E00]">
              Comprar €12
            </button>
          </div>
        </div>
      </div>

      {/* Track list */}
      <div>
        <div className="grid grid-cols-[40px_1fr_auto_auto] md:grid-cols-[40px_1fr_90px_120px_60px] gap-3 px-4 md:px-6 py-3 border-b border-[#27272A] font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
          <span>#</span>
          <span>TÍTULO</span>
          <span className="hidden md:inline text-right">BPM · KEY</span>
          <span className="text-right">PLAYS</span>
          <span className="text-right">
            <Clock size={11} className="inline" />
          </span>
        </div>
        <ul>
          {data.tracks.map((t, i) => {
            const active = playingId === t.id;
            const activePlaying = active && isPlaying;
            return (
              <li
                key={t.id}
                data-testid={`track-${t.id}`}
                onClick={() => playTrack(t.id)}
                className={`grid grid-cols-[40px_1fr_auto_auto] md:grid-cols-[40px_1fr_90px_120px_60px] gap-3 px-4 md:px-6 py-3 border-b border-[#27272A] last:border-b-0 items-center cursor-pointer transition-colors ${
                  active ? "bg-[#FF3E00]/10" : "hover:bg-[#0a0a0a]"
                }`}
              >
                <div
                  className={`font-mono text-xs tracking-wider ${
                    active ? "text-[#FF3E00]" : "text-[#A1A1AA]"
                  }`}
                >
                  {activePlaying ? (
                    <Pause size={14} weight="fill" />
                  ) : active ? (
                    <Play size={14} weight="fill" />
                  ) : (
                    String(i + 1).padStart(2, "0")
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-display font-bold truncate ${
                        active ? "text-[#FF3E00]" : ""
                      }`}
                    >
                      {t.title}
                    </span>
                    {t.featured ? (
                      <span className="font-mono text-[9px] uppercase tracking-[0.22em] bg-[#FF3E00] text-white px-1.5 py-0.5 shrink-0">
                        SINGLE
                      </span>
                    ) : null}
                    {t.explicit ? (
                      <span className="font-mono text-[9px] font-bold bg-[#A1A1AA] text-black px-1 py-0.5">
                        E
                      </span>
                    ) : null}
                    {!t.audio_url ? (
                      <span
                        title="Usando pista genérica de demo"
                        className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#525252]"
                      >
                        · demo
                      </span>
                    ) : null}
                  </div>
                  {active ? (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="font-mono text-[10px] text-[#FF3E00] tracking-wider tabular-nums">
                        {fmt(currentTime)}
                      </span>
                      <div className="flex-1 flex items-end gap-0.5 h-4 pointer-events-none">
                        {Array.from({ length: 60 }).map((_, b) => {
                          const isFilled = b < (pct * 60) / 100;
                          return (
                            <span
                              key={b}
                              className={`flex-1 ${
                                isFilled ? "bg-[#FF3E00]" : "bg-[#27272A]"
                              }`}
                              style={{
                                height: `${30 + ((b * 7) % 70)}%`,
                              }}
                            />
                          );
                        })}
                      </div>
                      <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider tabular-nums">
                        {fmt(duration || parseDurStr(t.duration))}
                      </span>
                    </div>
                  ) : null}
                </div>
                <span className="hidden md:inline font-mono text-[11px] text-[#A1A1AA] tracking-wider text-right">
                  {t.bpm} · {t.key}
                </span>
                <span className="font-mono text-[11px] text-[#A1A1AA] tracking-wider text-right">
                  {t.plays.toLocaleString("es")}
                </span>
                <span className="font-mono text-[11px] text-[#A1A1AA] tracking-wider text-right">
                  {t.duration}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Sticky classic player bar */}
      {currentTrack ? (
        <div
          data-testid="music-player-bar"
          className="fixed bottom-0 left-0 right-0 z-40 bg-[#050505] border-t border-[#27272A]"
        >
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-3 grid grid-cols-[1fr] md:grid-cols-[260px_1fr_220px] gap-4 items-center">
            {/* Left: track meta */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-12 w-12 bg-[#121212] border border-[#27272A] overflow-hidden shrink-0">
                <img
                  src={data.cover}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="min-w-0 hidden md:block">
                <div className="font-display font-bold text-sm truncate">
                  {currentTrack.title}
                </div>
                <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.2em] truncate">
                  {data.title} · {data.type}
                </div>
              </div>
            </div>

            {/* Center: controls + seek */}
            <div className="min-w-0">
              <div className="flex items-center justify-center gap-4 mb-1.5">
                <button
                  data-testid="player-prev"
                  onClick={prev}
                  className="h-8 w-8 inline-flex items-center justify-center text-[#A1A1AA] hover:text-white"
                >
                  <SkipBack size={16} weight="fill" />
                </button>
                <button
                  data-testid="player-playpause"
                  onClick={() => playTrack(currentTrack.id)}
                  className="h-10 w-10 inline-flex items-center justify-center bg-[#FF3E00] text-white hover:scale-105 transition-transform"
                >
                  {isPlaying ? (
                    <Pause size={16} weight="fill" />
                  ) : (
                    <Play size={16} weight="fill" />
                  )}
                </button>
                <button
                  data-testid="player-next"
                  onClick={next}
                  className="h-8 w-8 inline-flex items-center justify-center text-[#A1A1AA] hover:text-white"
                >
                  <SkipForward size={16} weight="fill" />
                </button>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider tabular-nums w-10 text-right">
                  {fmt(currentTime)}
                </span>
                <input
                  data-testid="player-seek"
                  type="range"
                  min={0}
                  max={duration || 0}
                  step={0.1}
                  value={currentTime}
                  onChange={seek}
                  className="flex-1 h-1 accent-[#FF3E00] cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #FF3E00 ${pct}%, #27272A ${pct}%)`,
                  }}
                />
                <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider tabular-nums w-10">
                  {fmt(duration)}
                </span>
              </div>
            </div>

            {/* Right: volume */}
            <div className="hidden md:flex items-center justify-end gap-3">
              <button
                data-testid="player-mute"
                onClick={() => setMuted((m) => !m)}
                className="text-[#A1A1AA] hover:text-white"
              >
                {muted || volume === 0 ? (
                  <SpeakerX size={16} weight="fill" />
                ) : (
                  <SpeakerHigh size={16} weight="fill" />
                )}
              </button>
              <input
                data-testid="player-volume"
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={muted ? 0 : volume}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setVolume(v);
                  if (v > 0) setMuted(false);
                }}
                className="w-24 h-1 accent-[#FF3E00] cursor-pointer"
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* Credits */}
      <div className="p-4 md:p-6 border-t border-[#27272A] grid grid-cols-2 md:grid-cols-4 gap-4 font-mono text-[11px]">
        <CreditRow k="PRODUCCIÓN" v="Lola Serrano & co-prod." />
        <CreditRow k="MASTERING" v="Abbey Road · Londres" />
        <CreditRow k="SELLO" v="Independiente" />
        <CreditRow k="ISRC" v="ES-7KN-26-00001" />
      </div>
    </div>
  );
}

function CreditRow({ k, v }) {
  return (
    <div>
      <div className="text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-1">{k}</div>
      <div className="text-[#F4F4F5] font-semibold tracking-wide">{v}</div>
    </div>
  );
}

const statusConfig = {
  applied: { label: "POSTULADA", color: "#A1A1AA", bg: "transparent" },
  shortlisted: { label: "SHORTLIST", color: "#FFD600", bg: "#FFD60015" },
  won: { label: "GANADA", color: "#00FF66", bg: "#00FF6615" },
  rejected: { label: "RECHAZADA", color: "#FF0033", bg: "#FF003315" },
};

function ApplicationsTab({ slug, applications }) {
  const credentials = getCredentials(slug);
  const artist = getArtist(slug) || { discipline: "MIXTO" };
  const won = applications.filter((a) => a.status === "won").length;
  const shortlisted = applications.filter((a) => a.status === "shortlisted").length;
  const pending = applications.filter((a) => a.status === "applied").length;
  // suggest similar calls by discipline
  const appliedIds = applications.map((a) => a.callId);
  const suggestions = allCalls
    .filter(
      (c) =>
        !appliedIds.includes(c.id) &&
        (c.discipline === artist.discipline || c.discipline === "MIXTO")
    )
    .slice(0, 3);

  return (
    <div className="p-4 md:p-6 space-y-8">
      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 border border-[#27272A]">
        <Stat n={applications.length} label="POSTULACIONES" />
        <Stat n={won} label="GANADAS" divider color="#00FF66" />
        <Stat n={shortlisted} label="SHORTLIST" divider color="#FFD600" />
        <Stat n={pending} label="PENDIENTES" divider />
      </div>

      {/* Applications */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Trophy size={18} className="text-[#FF3E00]" weight="fill" />
          <h3 className="font-display font-black uppercase tracking-tight text-xl">
            Historial
          </h3>
          <span className="ml-auto font-mono text-[10px] text-[#A1A1AA] tracking-wider">
            PÚBLICO · CV VERIFICADO
          </span>
        </div>
        <ul>
          {applications.map((a) => {
            const call = getCall(a.callId);
            const s = statusConfig[a.status];
            return (
              <li
                key={a.callId}
                data-testid={`application-${a.callId}`}
                className="flex items-start gap-4 p-4 border border-[#27272A] -mb-px bg-[#0a0a0a]"
              >
                <img
                  src={call.cover}
                  alt=""
                  className="h-16 w-16 object-cover border border-[#27272A] grayscale shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="font-mono text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5 border"
                      style={{ color: s.color, borderColor: s.color, background: s.bg }}
                    >
                      {s.label}
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A1A1AA]">
                      {call.type}
                    </span>
                  </div>
                  <Link
                    to={`/convocatorias/${call.id}`}
                    className="font-display font-bold text-base mt-2 block hover:text-[#FF3E00] line-clamp-2"
                  >
                    {call.title}
                  </Link>
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider mt-1">
                    {call.org.toUpperCase()} · POSTULADA {a.appliedAt}
                  </div>
                  {a.note ? (
                    <div
                      className="font-mono text-[10px] tracking-wider mt-2 italic"
                      style={{ color: s.color }}
                    >
                      // {a.note}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Verified credentials */}
      {credentials.length > 0 ? (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <SealCheck size={18} className="text-[#00FF66]" weight="fill" />
            <h3 className="font-display font-black uppercase tracking-tight text-xl">
              Credenciales verificadas
            </h3>
          </div>
          <ul className="border border-[#27272A]">
            {credentials.map((c, i) => (
              <li
                key={i}
                className="grid grid-cols-[80px_1fr_auto] gap-4 items-center px-4 py-3 border-b border-[#27272A] last:border-b-0"
              >
                <span className="font-mono text-[11px] text-[#FF3E00] tracking-wider font-bold">
                  {c.year}
                </span>
                <span className="font-display font-semibold text-sm">{c.label}</span>
                {c.verified ? (
                  <SealCheck size={14} weight="fill" className="text-[#00FF66]" />
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Suggestions */}
      {suggestions.length > 0 ? (
        <div>
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-3">
            // SUGERENCIAS PARA TI
          </div>
          <h3 className="font-display font-black uppercase tracking-tight text-xl mb-4">
            Según tu trayectoria en {artist.discipline}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {suggestions.map((c) => (
              <Link
                key={c.id}
                to={`/convocatorias/${c.id}`}
                className="block border border-[#27272A] hover:border-[#FF3E00] bg-[#0a0a0a] p-4 group"
              >
                <div className="font-mono text-[10px] text-[#FF3E00] tracking-wider">
                  {c.type} · {c.daysLeft}D
                </div>
                <div className="font-display font-bold text-sm mt-2 line-clamp-2 leading-tight group-hover:text-[#FF3E00]">
                  {c.title}
                </div>
                <div className="font-mono text-[10px] text-[#A1A1AA] mt-3 pt-3 border-t border-[#27272A] flex items-center justify-between">
                  <span className="truncate">{c.org}</span>
                  <span className="text-[#FF3E00] whitespace-nowrap">
                    {c.prize.length > 16 ? c.prize.slice(0, 14) + "…" : c.prize}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
